// src/components/AppSidebar.jsx
// Sidebar desktop + navegação inferior mobile.
// Inclui acesso às sessões pendentes e ao lançamento manual/retroativo.
// Ícones padronizados com lucide-react.

import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  BarChart3,
  CalendarDays,
  ChevronRight,
  Clock3,
  CircleDollarSign,
  FilePlus2,
  Home,
  UserRoundSearch,
  LogOut,
  MoreHorizontal,
  MessageCircle,
  Settings,
  ShieldCheck,
  Sparkles,
  UsersRound,
  WalletCards,
  X,
} from "lucide-react";

import Logo from "../assets/logoClaro.webp";
import "../sass/global/AppSidebar.scss";

const STORAGE_KEYS_TO_CLEAR = [
  "psy_token",
  "psy_id",
  "psy_usuario",
  "psy_email",
  "psy_nome",

  "plano",
  "valorPlano",
  "statusAssinatura",
  "assinaturaAtiva",
  "avisoAssinatura",
  "recursos",

  "stripePriceId",
  "currentPeriodEnd",
  "vencimentoAssinatura",
  "isTrial",
  "trialEndsAt",

  "planoAgendado",
  "valorPlanoAgendado",
  "stripePriceIdAgendado",
  "downgradeAgendado",
  "downgradeSolicitadoEm",
  "downgradeEfetivoEm",
  "downgradeScheduleId",

  "token",
  "id",
  "user",
  "usuario",
  "email",
];

const NAV_GROUPS = [
  {
    title: "Atendimento",
    items: [
      {
        to: "/dashboard",
        label: "Início",
        shortLabel: "Início",
        icon: Home,
        description: "Resumo do consultório",
      },
      {
        to: "/schedule",
        label: "Agenda",
        shortLabel: "Agenda",
        icon: CalendarDays,
        description: "Sessões e compromissos",
      },
      {
        to: "/open-sessions",
        label: "Sessões em aberto",
        shortLabel: "Em aberto",
        icon: Clock3,
        description: "Atendimentos aguardando encerramento",
      },
      // {
      //   to: "/session-control",
      //   label: "Sessões pendentes",
      //   shortLabel: "Pendentes",
      //   icon: Clock3,
      //   description: "Finalize atendimentos realizados",
      // },
      {
        to: "/launch-session",
        label: "Lançar sessão",
        shortLabel: "Lançar",
        icon: FilePlus2,
        description: "Sessão manual ou retroativa",
      },
      {
        to: "/clients",
        label: "Clientes",
        shortLabel: "Clientes",
        icon: UsersRound,
        description: "Prontuários e histórico",
      },
    ],
  },
  {
    title: "Gestão",
    items: [
      {
        to: "/finance",
        label: "Financeiro",
        shortLabel: "Finanças",
        icon: WalletCards,
        description: "Receitas, despesas e visão geral",
      },
      {
        to: "/cobranca",
        label: "Pagamentos pendentes",
        shortLabel: "Pendências",
        icon: CircleDollarSign,
        description: "Baixa manual e régua financeira",
      },
      {
        to: "/communications",
        label: "Central de Comunicações",
        shortLabel: "Comunicações",
        icon: MessageCircle,
        description: "E-mails, WhatsApps e status de entrega",
      },
      {
        to: "/reports",
        label: "Relatórios",
        shortLabel: "Relatórios",
        icon: BarChart3,
        description: "Indicadores e análises",
      },
      {
        to: "/leads",
        label: "Leads",
        shortLabel: "Leads",
        icon: UserRoundSearch,
        description: "Captação e oportunidades",
      },
    ],
  },
  {
    title: "Sistema",
    items: [
      {
        to: "/settings",
        label: "Configurações",
        shortLabel: "Ajustes",
        icon: Settings,
        description: "Preferências e conta",
      },
    ],
  },
];

const MOBILE_MAIN_ITEMS = [
  {
    to: "/dashboard",
    label: "Início",
    icon: Home,
  },
  {
    to: "/schedule",
    label: "Agenda",
    icon: CalendarDays,
  },
  {
    to: "/clients",
    label: "Clientes",
    icon: UsersRound,
  },
  {
    to: "/finance",
    label: "Financeiro",
    icon: WalletCards,
  },
];

const MOBILE_MORE_PATHS = [
  "/open-sessions",
  "/session-control",
  "/launch-session",
  "/cobranca",
  "/communications",
  "/reports",
  "/leads",
  "/settings",
  "/plan-management",
  "/help",
];

function readStorage(key) {
  return sessionStorage.getItem(key) || localStorage.getItem(key) || "";
}

function normalizeText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function formatDateBR(value) {
  if (!value) return "";

  try {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  } catch {
    return "";
  }
}

function getPlanLabel() {
  const plan = readStorage("plano") || "PsyAssist";
  const status = normalizeText(readStorage("statusAssinatura"));

  const downgradeAgendado =
    String(readStorage("downgradeAgendado")).toLowerCase() === "true";

  const planoAgendado = readStorage("planoAgendado");
  const downgradeEfetivoEm = readStorage("downgradeEfetivoEm");
  const dataEfetiva = formatDateBR(downgradeEfetivoEm);

  if (status === "past_due") {
    return `${plan} • pagamento pendente`;
  }

  if (downgradeAgendado && planoAgendado) {
    return dataEfetiva
      ? `${plan} • muda para ${planoAgendado} em ${dataEfetiva}`
      : `${plan} • downgrade agendado`;
  }

  return plan;
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "PS";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function isPathActive(pathname, to) {
  if (!to) return false;

  if (to === "/dashboard") {
    return pathname === "/" || pathname === "/dashboard";
  }

  return pathname === to || pathname.startsWith(`${to}/`);
}

function MenuItem({
  to,
  label,
  icon: Icon,
  description,
  onClick,
  pathname,
}) {
  const active = isPathActive(pathname, to);

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={`sb-item ${active ? "sb-item--active" : ""}`}
      aria-current={active ? "page" : undefined}
    >
      <span className="sb-item__ico" aria-hidden="true">
        <Icon size={20} strokeWidth={2.15} />
      </span>

      <span className="sb-item__body">
        <span className="sb-item__lbl">{label}</span>

        {description ? (
          <span className="sb-item__desc">{description}</span>
        ) : null}
      </span>

      <span className="sb-item__chev" aria-hidden="true">
        <ChevronRight size={18} strokeWidth={2.2} />
      </span>
    </NavLink>
  );
}

function MobileNavItem({
  to,
  label,
  icon: Icon,
  onClick,
  pathname,
}) {
  const active = isPathActive(pathname, to);

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={`sb-mobile-item ${
        active ? "sb-mobile-item--active" : ""
      }`}
      aria-current={active ? "page" : undefined}
    >
      <span className="sb-mobile-item__ico" aria-hidden="true">
        <Icon size={21} strokeWidth={2.15} />
      </span>

      <span className="sb-mobile-item__lbl">{label}</span>
    </NavLink>
  );
}

export default function AppSidebar({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [moreOpen, setMoreOpen] = useState(false);

  const userName = useMemo(
    () => readStorage("psy_usuario") || "PsyAssist",
    []
  );

  const plan = useMemo(() => getPlanLabel(), []);
  const navGroups = useMemo(() => NAV_GROUPS, []);

  const activeNavItem = useMemo(() => {
    return navGroups
      .flatMap((group) => group.items)
      .find((item) =>
        isPathActive(
          location.pathname,
          item.to
        )
      );
  }, [
    location.pathname,
    navGroups,
  ]);

  const moreActive = MOBILE_MORE_PATHS.some((path) =>
    isPathActive(location.pathname, path)
  );

  const panelOpen = Boolean(open || moreOpen);

  const closePanel = () => {
    setMoreOpen(false);

    if (typeof onClose === "function") {
      onClose();
    }
  };

  const closeOnNavigate = () => {
    closePanel();
  };

  useEffect(() => {
    if (panelOpen) {
      document.body.classList.add("sb-lock");
    } else {
      document.body.classList.remove("sb-lock");
    }

    return () => {
      document.body.classList.remove("sb-lock");
    };
  }, [panelOpen]);

  useEffect(() => {
    closePanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && panelOpen) {
        closePanel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpen]);

  const logout = () => {
    STORAGE_KEYS_TO_CLEAR.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    closePanel();
    navigate("/login", { replace: true });
  };

  const openMore = () => {
    setMoreOpen(true);
  };

  const go = (path) => {
    closePanel();
    navigate(path);
  };

  return (
    <>
      <button
        className={`sb-overlay ${panelOpen ? "sb-overlay--open" : ""}`}
        onClick={closePanel}
        type="button"
        aria-label="Fechar menu"
        tabIndex={panelOpen ? 0 : -1}
      />

      <aside
        className={`sb ${open ? "sb--open" : ""}`}
        aria-hidden={!open}
        aria-label="Menu principal do PsyAssist"
      >
        <div className="sb-shell">
          <header className="sb-header">
            <div className="sb-brand">
              <img src={Logo} alt="PsyAssist" className="sb-logo" />

              <div className="sb-brand__txt">
                <strong>PsyAssist Pro</strong>
                <span>
                  {activeNavItem?.label ||
                    "Workspace clínico"}
                </span>
              </div>
            </div>

            <button
              className="sb-close"
              onClick={closePanel}
              type="button"
              aria-label="Fechar menu"
              title="Fechar menu"
            >
              <X size={19} strokeWidth={2.2} />
            </button>
          </header>

          <section className="sb-profile">
            <div className="sb-profile__top">
              <div className="sb-profile__avatar">
                {getInitials(userName)}
              </div>

              <div className="sb-profile__info">
                <span>Conectado como</span>
                <strong>{userName}</strong>
              </div>

              <div
                className="sb-profile__verified"
                title="Sessão autenticada"
                aria-label="Sessão autenticada"
              >
                <BadgeCheck
                  size={18}
                  strokeWidth={2.15}
                  aria-hidden="true"
                />
              </div>
            </div>

            <div
              className="sb-profile__plan"
              title={plan}
            >
              <Sparkles
                size={14}
                strokeWidth={2.15}
                aria-hidden="true"
              />

              <span>{plan}</span>
            </div>
          </section>

          <nav className="sb-nav" aria-label="Navegação principal">
            {navGroups.map((group) => (
              <section className="sb-section" key={group.title}>
                <div className="sb-title">{group.title}</div>

                <div className="sb-list">
                  {group.items.map((item) => (
                    <MenuItem
                      key={item.to}
                      to={item.to}
                      label={item.label}
                      icon={item.icon}
                      description={item.description}
                      onClick={closeOnNavigate}
                      pathname={location.pathname}
                    />
                  ))}
                </div>
              </section>
            ))}
          </nav>

          <footer className="sb-footer">
            <div className="sb-footer__security">
              <ShieldCheck
                size={18}
                strokeWidth={2.15}
                aria-hidden="true"
              />

              <div>
                <strong>Ambiente protegido</strong>
                <span>
                  Sua sessão clínica está autenticada.
                </span>
              </div>
            </div>

            <button
              className="sb-logout"
              onClick={logout}
              type="button"
            >
              <LogOut
                size={18}
                strokeWidth={2.2}
                aria-hidden="true"
              />
              Sair da conta
            </button>
          </footer>
        </div>
      </aside>

      <nav className="sb-mobile-nav" aria-label="Menu inferior">
        {MOBILE_MAIN_ITEMS.map((item) => (
          <MobileNavItem
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            onClick={closeOnNavigate}
            pathname={location.pathname}
          />
        ))}

        <button
          className={`sb-mobile-item ${
            moreActive || moreOpen ? "sb-mobile-item--active" : ""
          }`}
          type="button"
          onClick={openMore}
          aria-label="Abrir mais opções"
          aria-expanded={moreOpen || open}
        >
          <span className="sb-mobile-item__ico" aria-hidden="true">
            <MoreHorizontal size={22} strokeWidth={2.2} />
          </span>

          <span className="sb-mobile-item__lbl">Mais</span>
        </button>
      </nav>

      <section
        className={`sb-mobile-sheet ${
          panelOpen ? "sb-mobile-sheet--open" : ""
        }`}
        aria-hidden={!panelOpen}
        aria-label="Mais opções"
      >
        <div className="sb-mobile-sheet__handle" aria-hidden="true" />

        <header className="sb-mobile-sheet__head">
          <div className="sb-mobile-sheet__brand">
            <img src={Logo} alt="PsyAssist" />

            <div>
              <strong>Mais opções</strong>
              <span>
                {activeNavItem?.label ||
                  "Workspace clínico"}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={closePanel}
            aria-label="Fechar menu"
            title="Fechar menu"
          >
            <X size={18} strokeWidth={2.2} />
          </button>
        </header>

        <section className="sb-mobile-sheet__profile">
          <div className="sb-profile__avatar">
            {getInitials(userName)}
          </div>

          <div>
            <span>Conectado como</span>
            <strong>{userName}</strong>
          </div>

          <div
            className="sb-mobile-sheet__plan"
            title={plan}
          >
            <Sparkles
              size={13}
              strokeWidth={2.15}
              aria-hidden="true"
            />
            <span>{plan}</span>
          </div>
        </section>

        <div className="sb-mobile-sheet__groups">
          {navGroups.map((group) => {
            const mobileItems =
              group.items.filter(
                (item) =>
                  !MOBILE_MAIN_ITEMS.some(
                    (mainItem) =>
                      mainItem.to === item.to
                  )
              );

            if (!mobileItems.length) {
              return null;
            }

            return (
              <section
                className="sb-mobile-sheet__section"
                key={group.title}
              >
                <div className="sb-mobile-sheet__title">
                  {group.title}
                </div>

                <div className="sb-mobile-sheet__grid">
                  {mobileItems.map((item) => {
                    const Icon = item.icon;

                    const active =
                      isPathActive(
                        location.pathname,
                        item.to
                      );

                    return (
                      <button
                        key={item.to}
                        type="button"
                        className={`sb-sheet-item ${
                          active
                            ? "sb-sheet-item--active"
                            : ""
                        }`}
                        onClick={() =>
                          go(item.to)
                        }
                        aria-current={
                          active
                            ? "page"
                            : undefined
                        }
                      >
                        <span
                          className="sb-sheet-item__ico"
                          aria-hidden="true"
                        >
                          <Icon
                            size={20}
                            strokeWidth={2.15}
                          />
                        </span>

                        <span>
                          <strong>
                            {item.label}
                          </strong>
                          <small>
                            {item.description}
                          </small>
                        </span>

                        <ChevronRight
                          className="sb-sheet-item__chevron"
                          size={17}
                          strokeWidth={2.15}
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        <button
          className="sb-mobile-logout"
          type="button"
          onClick={logout}
        >
          <LogOut size={18} strokeWidth={2.2} aria-hidden="true" />
          Sair da conta
        </button>
      </section>
    </>
  );
}
